import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';

const ShareModal = ({ credential, isOpen, onClose, onShare }) => {
  const [selectedFields, setSelectedFields] = useState([]);
  const [shareMethod, setShareMethod] = useState('qr');
  const [expirationTime, setExpirationTime] = useState('1h');
  const [requireVerification, setRequireVerification] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const qrRef = useRef(null);

  const shareMethodOptions = [
    { value: 'qr', label: 'QR Code' },
    { value: 'link', label: 'Secure Link' },
    { value: 'email', label: 'Email' }
  ];

  const expirationOptions = [
    { value: '15m', label: '15 minutes' },
    { value: '1h', label: '1 hour' },
    { value: '24h', label: '24 hours' },
    { value: '7d', label: '7 days' },
    { value: 'never', label: 'Never expires' }
  ];

  const availableFields = [
    { id: 'name', label: 'Full Name', required: true },
    { id: 'issueDate', label: 'Issue Date', required: false },
    { id: 'expirationDate', label: 'Expiration Date', required: false },
    { id: 'issuer', label: 'Issuer', required: true },
    { id: 'verificationStatus', label: 'Verification Status', required: true },
    ...credential?.details?.map(detail => ({
      id: detail?.label?.toLowerCase()?.replace(/\s+/g, '_'),
      label: detail?.label,
      required: false
    }))
  ];

  const handleFieldToggle = (fieldId) => {
    setSelectedFields(prev => {
      if (prev?.includes(fieldId)) {
        return prev?.filter(id => id !== fieldId);
      } else {
        return [...prev, fieldId];
      }
    });
  };

  const generateQRCode = () => {
    // Mock QR code generation
    const mockQRData = `https://healthguard.pro/verify/${credential?.id}?fields=${selectedFields?.join(',')}&expires=${expirationTime}&verify=${requireVerification}`;
    setQrCode(mockQRData);
  };

  const handleShare = () => {
    const shareData = {
      credentialId: credential?.id,
      selectedFields,
      shareMethod,
      expirationTime,
      requireVerification
    };
    onShare(shareData);
  };

  const downloadQR = () => {
    // Mock QR code download functionality
    const canvas = document.createElement('canvas');
    const ctx = canvas?.getContext('2d');
    canvas.width = 200;
    canvas.height = 200;
    
    // Simple QR code representation
    ctx.fillStyle = '#000000';
    for (let i = 0; i < 20; i++) {
      for (let j = 0; j < 20; j++) {
        if (Math.random() > 0.5) {
          ctx?.fillRect(i * 10, j * 10, 10, 10);
        }
      }
    }
    
    const link = document.createElement('a');
    link.download = `${credential?.title}-qr-code.png`;
    link.href = canvas?.toDataURL();
    link?.click();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1200 p-4">
      <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Share Credential</h2>
            <p className="text-muted-foreground">{credential?.title}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-clinical"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Selective Disclosure */}
          <div>
            <h3 className="font-medium text-foreground mb-3">Select Information to Share</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Choose which information to include in the shared credential. Required fields are automatically included.
            </p>
            <div className="space-y-3 max-h-48 overflow-y-auto border border-border rounded-lg p-4">
              {availableFields?.map((field) => (
                <div key={field?.id} className="flex items-center justify-between">
                  <Checkbox
                    label={field?.label}
                    checked={field?.required || selectedFields?.includes(field?.id)}
                    onChange={() => !field?.required && handleFieldToggle(field?.id)}
                    disabled={field?.required}
                  />
                  {field?.required && (
                    <span className="text-xs text-muted-foreground">Required</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Share Method */}
          <div>
            <Select
              label="Share Method"
              options={shareMethodOptions}
              value={shareMethod}
              onChange={setShareMethod}
            />
          </div>

          {/* Expiration Time */}
          <div>
            <Select
              label="Link Expiration"
              description="How long should the shared credential be accessible?"
              options={expirationOptions}
              value={expirationTime}
              onChange={setExpirationTime}
            />
          </div>

          {/* Verification Requirement */}
          <div>
            <Checkbox
              label="Require recipient verification"
              description="Recipient must verify their identity before accessing the credential"
              checked={requireVerification}
              onChange={(e) => setRequireVerification(e?.target?.checked)}
            />
          </div>

          {/* QR Code Preview */}
          {shareMethod === 'qr' && qrCode && (
            <div className="bg-muted rounded-lg p-6 text-center">
              <div className="w-48 h-48 bg-white border-2 border-border rounded-lg mx-auto mb-4 flex items-center justify-center">
                <div ref={qrRef} className="w-40 h-40 bg-black" style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Cpath d='M0 0h10v10H0zm20 0h10v10H20zm40 0h10v10H40zm60 0h10v10H60zm80 0h10v10H80zM10 10h10v10H10zm30 10h10v10H30zm50 10h10v10H50zm70 10h10v10H70zm90 10h10v10H90z' fill='%23000'/%3E%3C/svg%3E")`,
                  backgroundSize: 'cover'
                }} />
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Scan this QR code to access the shared credential
              </p>
              <Button
                variant="outline"
                size="sm"
                iconName="Download"
                iconPosition="left"
                onClick={downloadQR}
              >
                Download QR Code
              </Button>
            </div>
          )}

          {/* Zero-Knowledge Proof Options */}
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Shield" size={16} className="text-accent" />
              <h4 className="font-medium text-foreground">Zero-Knowledge Proof Options</h4>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              Advanced privacy features that allow verification without revealing sensitive data
            </p>
            <div className="space-y-2">
              <Checkbox
                label="Age verification only (hide birth date)"
                description="Prove age requirements without revealing exact birth date"
              />
              <Checkbox
                label="Vaccination validity only (hide medical details)"
                description="Prove vaccination status without revealing specific medical information"
              />
              <Checkbox
                label="Range proof for lab results"
                description="Prove results are within safe parameters without revealing exact values"
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <div className="flex items-center space-x-3">
            {shareMethod === 'qr' && !qrCode && (
              <Button
                variant="outline"
                iconName="QrCode"
                iconPosition="left"
                onClick={generateQRCode}
              >
                Generate QR Code
              </Button>
            )}
            <Button
              variant="default"
              iconName="Share"
              iconPosition="left"
              onClick={handleShare}
              disabled={selectedFields?.length === 0}
            >
              Share Credential
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;